import 'es6-promise'
import uuid from 'uuid-random'
import { Observable } from 'rxjs'
import { first } from 'rxjs/operators'
import { userInfoURL } from '../../constants/ApiConstants'
import { IsVarEmpty } from '../../utils/GlobalFunctions'
import { requestGetJSON } from '../API/REST/RESTAPIClient'
import { RxJS } from './RxJS'
import { RxJSState } from './RxJSState'
import { disabledFlags$ } from './DisabledFlagsState'
import { userInfoErrorState$ } from './UserInfoErrorState'
import { AdminUserResponse, CurrentUserResponse, JSONResponse } from '../Types/Types'
import { Page } from '../../constants/Components/Page'
import { getPageName } from '../DataIngest'

const CACHING_PERIOD: number = 3000

/**
 * Call the current user endpoint and get the required data.
 *
 * @param isCacheBusted - Override the cache and get the data from the server.
 * @param errorHandler - Error handler.
 */
export const retrieveUserInfo = (
    isCacheBusted?: boolean,
    errorHandler?: (error: Error) => void
): Promise<Nullable<CurrentUserResponse>> =>
    requestGetJSON<CurrentUserResponse>(
        userInfoURL.appendQueryParams(
            isCacheBusted
                ? {
                      cacheBuster: uuid()
                  }
                : {}
        ),
        { cacheTime: isCacheBusted ? undefined : CACHING_PERIOD }
    )
        .then((res: JSONResponse<CurrentUserResponse>): JSONResponse<CurrentUserResponse> => {
            RxJS.currentUserState?.announce(res?.Data)

            return res
        })
        .catch((e: Error) => {
            userInfoErrorState$.announce(true)
            // This uses the new useDisabledFlags hook to check if the error should be displayed.
            disabledFlags$.observable
                .pipe(first())
                .subscribe((disabledFlags: Definable<Record<string, Definable<boolean>>>) => {
                    if (
                        (IsVarEmpty(getPageName()) || getPageName() !== Page.WELCOME) &&
                        !disabledFlags?.disableUserInfoErrors
                    ) {
                        errorHandler?.(e)
                    }
                })

            return null
        })

RxJS.currentUserState =
    RxJS.currentUserState ??
    RxJSState.newInstance<AdminUserResponse, () => Promise<Nullable<CurrentUserResponse>>>('userInfo', retrieveUserInfo)

export const currentUserState$ = RxJS.currentUserState

/**
 * Single call to get the current user info and resolve the observable and unsubscribe.
 */
export const resolveAndGetCurrentUserInfo = (): Promise<Definable<AdminUserResponse>> =>
    currentUserState$.resolver?.(true).then((res: Nullable<CurrentUserResponse>) => res?.Data) ?? Promise.reject()

/**
 * Get the current user info observable from the window.
 *
 * @param errorHandler
 * @param isCacheBusted - Override the cache and get the data from the server.
 *
 * @returns - Observable of the current user data from the server.
 *
 * @deprecated - Use the currentUserState$ resolver function to re-get the data or just subscribe to get the data the first time.
 */
export const getCurrentUserInfo = (
    errorHandler?: (error: Error) => void,
    isCacheBusted?: boolean
): Observable<Definable<AdminUserResponse>> => currentUserState$.observable(isCacheBusted, errorHandler)
